import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NMFTA_INPUT_WRAPPER_ERROR_MESSAGES, NMFTAInputWrapperErrorMessageMap, } from '@nmfta/nmfta-ui';

type MinMaxLenError = { requiredLength?: number; actualLength?: number };
type DateError = { requiredDate?: Date };
type MultiEmailError = { invalidEmails?: string[] };
type MinMaxError = { min?: number; max?: number };

function buildInputWrapperErrorMessages(
  translate: TranslateService
): NMFTAInputWrapperErrorMessageMap {
  return {
    required: () => translate.instant('Generic_RequiredFieldError'),
    email: () => translate.instant('Generic_EmailError'),
    emailExists: () => translate.instant('Generic_EmailExistsError'),
    notZero: () => translate.instant('Generic_NotZeroError'),
    invalidTime: () => translate.instant('Generic_TimeError'),
    validationFailed: () => translate.instant('Generic_ValidationFailed'),
    pattern: () => translate.instant('Generic_InvalidValueError'),
    mask: () => translate.instant('Generic_InvalidValueError'),
    noWhiteSpaces: () => translate.instant('Generic_NoWhiteSpaceError'),
    specialCharacters: () => translate.instant('Generic_SpecialCharactersError'),
    creditCardError: () => translate.instant('Generic_InvalidValueError'),
    expiredMonth: () => translate.instant('Generic_ExpiredMonth'),
    expiredYear: () => translate.instant('Generic_ExpiredYear'),
    invalidPhone: () => translate.instant('Generic_InvalidValueError'),
    dateRangeOrder: () => translate.instant('Generic_DateRangeOrder'),
    invalidYear: (error: unknown) => {
      const e = error as { minYear?: number; maxYear?: number };
      return translate.instant('Generic_InvalidYear', { minYear: e?.minYear, maxYear: e?.maxYear });
    },

    matDatepickerParse: () => translate.instant('Generic_InvalidValueError'),
    matDatepickerMin: () => translate.instant('Generic_InvalidMinDate'),
    matDatepickerMax: () => translate.instant('Generic_InvalidMaxDate'),
    matEndDateInvalid: () => translate.instant('Generic_InvalidEndDate'),
    matStartDateInvalid: () => translate.instant('Generic_InvalidStartDate'),
    invalidArticleNumber: () => translate.instant('Generic_InvalidArticleNumberError'),
    duplicateSectionName: () => translate.instant('CREATE_PROPOSAL.DUPLICATE_SECTION_MESSAGE'),

    minlength: (error: unknown) => {
      const e = error as MinMaxLenError;
      return `${translate.instant('Generic_MinLengthError')} ${e?.requiredLength ?? ''}`;
    },

    maxlength: (error: unknown) => {
      const e = error as MinMaxLenError;
      return `${translate.instant('Generic_MaxLengthError')} ${e?.actualLength ?? 0}/${e?.requiredLength ?? 0}`;
    },

    amexCVVLength: (error: unknown) => {
      const e = error as MinMaxLenError;
      return `${translate.instant('Generic_MinLengthError')} ${e?.requiredLength ?? ''}`;
    },

    minDate: (error: unknown) => {
      const e = error as DateError;
      return `${translate.instant('Generic_MinDateError')} ${e?.requiredDate?.toLocaleDateString() ?? ''}`;
    },

    maxDate: (error: unknown) => {
      const e = error as DateError;
      return `${translate.instant('Generic_MaxDateError')} ${e?.requiredDate?.toLocaleDateString() ?? ''}`;
    },

    multipleEmailsExists: (error: unknown) => {
      const e = error as MultiEmailError;
      return `${translate.instant('Generic_MultipleEmailExistsError')} ${(e?.invalidEmails ?? []).join(', ')}`;
    },

    min: (error: unknown) => {
      const e = error as MinMaxError;
      return `${translate.instant('Generic_MinError')} ${e?.min ?? ''}`;
    },

    max: (error: unknown) => {
      const e = error as MinMaxError;
      return `${translate.instant('Generic_MaxError')} ${e?.max ?? ''}`;
    },

    sequentialDateRequired: () => translate.instant('Generic_DateSequenceRequired'),

    sequentialDateOrder: () => translate.instant('Generic_DateSequenceOrder')
  };
}

export function provideNMFTAInputWrapperErrorMessages(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: NMFTA_INPUT_WRAPPER_ERROR_MESSAGES,
      deps: [TranslateService],
      useFactory: (translate: TranslateService) => buildInputWrapperErrorMessages(translate),
    },
  ]);
}
